import { useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import CreatableSelect from 'react-select/creatable';
import { Card, CardBody, CardHeader } from 'reactstrap';

import Loader from 'components/Loader';
import { useK2Loader } from 'hooks/useK2Loader';
import K2Api from 'lib/ajax/K2Api';

import AdvancedTable from 'components/LM/common/AdvancedTable';
import Breadcrumb from "components/LM/common/Breadcrumb";
import ExportCSV from 'components/LM/common/ExportCSV';
import GoTopButton from 'components/LM/common/GoTopButton';
import './DetailStatus.scss';

const TABLE_COLUMNS = [
	{
		name: "voice_link",
		alias: "Voice ID"
	},
	{
		name: "voice_name",
		alias: "Voice name",
	},
	{
		name: "status",
		alias: "Status"
	},
	{
		name: "message",
		alias: "Log message"
	}
];

const CSV_COLUMNS = [
	{
		id: 'id',
		displayName: 'Id'
	},
	{
		id: 'name',
		displayName: 'Voice name'
	},
	{
		id: 'status',
		displayName: 'Status'
	},
	{
		id: 'logMessage',
		displayName: 'LogMessage'
	},
];

const k2Api = new K2Api();
function DetailStatus(props) {
	const { id } = props;

	const [list, setList] = useState(null);
	const [voices, setVoices] = useState([]);
	const [errors, setErrors] = useState(null);
	const [savedChanges, setSavedChanges] = useState(false);
	const [saving, setSaving] = useState(false);

	const [loadingDetails, detailsResponse, errorDetails] = useK2Loader(`/voices/import/${id}`);

	const onSubmitValidationList = async () => {
		setErrors(null);
		setSaving(true);
		let listId;
		if (list.new) {
			try {
				const response = await k2Api.post('/lists', { name: list.name });
				listId = response.data;

				updateSelectedList(list, listId);
			} catch (error) {
				const status = error.response.status;
				const message = error.response.data;
				if (status === 400) {
					setErrors({ saveList: message })
				} else if (status === 409) {
					setErrors({ saveList: `Duplicate list: ${message}` });
				}
				return;
			};
		} else {
			listId = list.id;
		}

		k2Api.put(`/voices/lists/${listId}`, voices)
			.then(setSavedChanges(true))
			.catch(error => {
				const message = error.response.data.errors[0];
				setErrors({ saveVoiceList: message });
			})
			.finally(() => setSaving(false));
	}

	const updateSelectedList = (list, newId) => {
		delete list.new;
		list.id = newId;
		setList({ ...list, });
	}

	let form = null;
	let button;
	if (savedChanges) {
		button = <Button className="lists-button" variant="success" disabled={true}>Saved</Button>;
	} else {
		button = <Button className="lists-button" disabled={!list || !voices.length || saving} onClick={onSubmitValidationList}>Save list</Button>;
	}
	form = (
		// Temporarily hidden – enable when "Save list" feature is functional
		<Form>
			<Form.Group className="form-inline" style={{ display: 'none' }}>
				<SelectValidationList
					disabled={voices.length ? false : true}
					list={list}
					onChange={(list) => {
						setList(list);
						setSavedChanges(false);
					}}
				/>
				{button}
			</Form.Group>
			{errors && <GenericError error={errors.saveList} />}
			{errors && <GenericError error={errors.saveVoiceList} />}
		</Form>
	);

	const onHandleSelectVoices = (voices) => {
		setVoices(voices);
		setSavedChanges(false);
	}

	function processApiResponse(response) {
		const total = response.uploaded_voices.length;
		const rows = response.uploaded_voices.map((uploadVoice, index) => {
			return {
				id: uploadVoice.voice.id ?? "-",
				file_id: uploadVoice.id,
				voice_link: <VoiceLink id={uploadVoice.voice.id} />,
				voice_name: uploadVoice.voice.name ?? "-",
				status: uploadVoice.status,
				message: uploadVoice.messages.join(', ')
			}
		});

		return { total, rows };
	}

	const renderTable = useMemo(() => {
		if (!detailsResponse) return null;

		return (
			<AdvancedTable
				hidePagination={true}
				selectable={true}
				columns={TABLE_COLUMNS}
				onSelectRows={onHandleSelectVoices}
				data={processApiResponse(detailsResponse)}
			/>
		);
	}, [detailsResponse]);

	if (loadingDetails) return <Loader />
	if (errorDetails) return <Alert variant="danger">{errorDetails.toString()}</Alert>

	return (
		<>
			{breadcrumbPage(detailsResponse.filename)}
			<Card>
				<CardHeader>
					<Row>
						<Col>
							Filename: <strong>{detailsResponse.filename}</strong>
							<br />
							Date: {detailsResponse.date}
						</Col>
						<Col>{form}</Col>
					</Row>
				</CardHeader>
				<CardBody>
					<div className="d-flex justify-content-end">
						<ExportCSV filename={detailsResponse.filename} buttonText="Download results in CSV" columns={CSV_COLUMNS} datas={prepareCSVToDownload(detailsResponse.uploaded_voices)} />
					</div>
					{renderTable}
				</CardBody>
			</Card>
			<GoTopButton />
		</>
	);
}

function SelectValidationList(props) {
	const { list, disabled } = props;
	const { onChange } = props;

	const [loading, lists] = useK2Loader('/lists');
	if (loading) return null;

	const onHandleChange = (ev) => {
		const list = { id: ev.value, name: ev.label, new: ev.__isNew__ || false };
		if (ev.__isNew__) {
			lists.push(list);
		}

		onChange(list);
	}

	const value = list ? { label: list.name, value: list.id } : null;
	const options = lists.map(list => { return { label: list.name, value: list.id } });

	return <CreatableSelect
		isDisabled={disabled}
		className='lists-selector'
		value={value}
		options={options}
		onChange={onHandleChange}
	/>
}

function VoiceLink(props) {
	if (!props.id) return null
	return <Link to={`/voices/${props.id}`} target="_blank" rel="noopener noreferrer">{props.id}</Link>
}

function GenericError(props) {
	if (!props.error) return null;
	return <p className="mb-0 text-warning">
		<em>{props.error}</em>
	</p>
}

function prepareCSVToDownload(uploadedVoices) {
	return uploadedVoices.map(row => {
		return {
			id: row.voice.id,
			name: row.voice.name,
			status: row.status,
			logMessage: row.messages.join(', ')
		};
	});
}

function breadcrumbPage(filename) {
	const items = [
		"Voices",
		<a href="/voices/import">Mass Import</a>,
		<a href="/voices/import/status">Import Status</a>,
		filename
	];
	return <Breadcrumb items={items} />
}

export default DetailStatus;
